import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import "../styles/MainProvider.css";
import defaultAvatar from "../assets/avatar.png";
import { UserBackground, TherapistProfileData } from "../types";
import { API_ENDPOINTS } from "../config";

/**
 * Normalize session fees to handle both old and new formats
 * Old format: {individual_min: "100", individual_max: "150", couples_min: "200", sliding_scale: true}
 * New format: {"Individual": "$150", "Couple": "$200", "Sliding Scale": "Available"}
 */
function normalizeFees(rawFees: any): Record<string, string> {
  if (!rawFees || typeof rawFees !== 'object') {
    return {};
  }

  // Check if this is the new format (has capitalized keys like "Individual", "Couple")
  const hasNewFormat = Object.keys(rawFees).some(key =>
    key === "Individual" || key === "Couple" || key === "Sliding Scale"
  );

  if (hasNewFormat) {
    // New format - return as-is
    return rawFees;
  }

  // Old format - convert to new format
  const normalized: Record<string, string> = {};

  // Handle individual fees
  if (rawFees.individual_min || rawFees.individual_max) {
    if (rawFees.individual_min && rawFees.individual_max) {
      normalized["Individual"] = `$${rawFees.individual_min} - $${rawFees.individual_max}`;
    } else if (rawFees.individual_min) {
      normalized["Individual"] = `$${rawFees.individual_min}`;
    } else if (rawFees.individual_max) {
      normalized["Individual"] = `$${rawFees.individual_max}`;
    }
  }

  // Handle couples fees
  if (rawFees.couples_min || rawFees.couples_max) {
    if (rawFees.couples_min && rawFees.couples_max) {
      normalized["Couple"] = `$${rawFees.couples_min} - $${rawFees.couples_max}`;
    } else if (rawFees.couples_min) {
      normalized["Couple"] = `$${rawFees.couples_min}`;
    } else if (rawFees.couples_max) {
      normalized["Couple"] = `$${rawFees.couples_max}`;
    }
  }

  // Handle sliding scale
  if (rawFees.sliding_scale === true || rawFees.sliding_scale === "true") {
    normalized["Sliding Scale"] = "Available";
  }

  return normalized;
}

const MainProvider: React.FC = () => {
  const { state, logout } = useApp();
  const { user } = state;

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<TherapistProfileData | null>(null);
  const [saving, setSaving] = useState(false);

  // Mock data, should be fetched from API
  const providerStats = {
    monthlyIncome: 5000,
    monthlySessionCount: 45,
    pendingWithdrawal: 2500,
    totalClients: 120,
    averageRating: 4.8,
    completedSessions: 450,
  };

  // Check if user has profile_data (registered therapist with complete profile)
  const hasProfileData = user && "profile_data" in user && user.profile_data;
  const profileData = hasProfileData ? (user.profile_data as TherapistProfileData) : null;

  // Extract data from profile_data
  let displayName: string, displayTitle: string, displayEducation: string[], displayTherapyMethods: string[], displaySpecialties: string[];
  let displayLocation: string, displayPhone: string, displayLanguages: string[], displayInsurance: string[], displayFees: any, displayPhoto: string | null;
  let displaySelfDescription: string, displayYearsExperience: string;

  if (profileData) {
    // Use profile_data (baseline fields from therapist table)
    displayName = profileData.name || "";
    displayTitle = profileData.title || "";
    displayLocation = profileData.location || "";
    displayPhone = profileData.phone || "";
    displayYearsExperience = String(profileData.years_practicing || "");
    displaySelfDescription = profileData.self_description || "";

    // Parse JSONB arrays
    displayTherapyMethods = Array.isArray(profileData.treatment_approach)
      ? profileData.treatment_approach
      : [];
    displaySpecialties = Array.isArray(profileData.specialties)
      ? profileData.specialties
      : [];
    displayLanguages = Array.isArray(profileData.languages) ? profileData.languages : [];
    displayInsurance = Array.isArray(profileData.insurance_accepted) ? profileData.insurance_accepted : [];

    // Education
    displayEducation = Array.isArray(profileData.education)
      ? profileData.education
      : [];

    // Session fees - handle both old format (individual_min/max) and new format ({"Individual": "$150"})
    const rawFees = profileData.session_fee || {};
    displayFees = normalizeFees(rawFees);

    // Photo
    displayPhoto = Array.isArray(profileData.photo_urls) && profileData.photo_urls.length > 0
      ? profileData.photo_urls[0]
      : null;
  } else {
    // No profile data yet (new user who hasn't completed questionnaire)
    displayName = user?.name || "";
    displayTitle = "";
    displayEducation = [];
    displayTherapyMethods = [];
    displaySpecialties = [];
    displayLocation = "";
    displayPhone = "";
    displayLanguages = [];
    displayInsurance = [];
    displayFees = {};
    displayPhoto = null;
    displaySelfDescription = "";
    displayYearsExperience = "";
  }

  // Handler for entering edit mode
  const handleEdit = () => {
    if (profileData) {
      setEditFormData({ ...profileData });
      setIsEditing(true);
    } else {
      // User hasn't completed their profile yet
      alert(
        "Please complete your profile setup first by filling out the questionnaire."
      );
      console.error("Cannot edit: profileData is null. User needs to complete questionnaire first.");
    }
  };

  // Handler for canceling edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData(null);
  };

  // Handler for saving profile updates
  const handleSave = async () => {
    if (!editFormData) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("token");

      // Prepare update payload - only send editable fields
      const updatePayload: any = {};

      if (editFormData.name) updatePayload.name = editFormData.name;
      if (editFormData.title) updatePayload.title = editFormData.title;
      if (editFormData.phone) updatePayload.phone = editFormData.phone;
      if (editFormData.location) updatePayload.location = editFormData.location;
      if (editFormData.self_description) updatePayload.self_description = editFormData.self_description;
      if (editFormData.years_practicing) updatePayload.years_practicing = editFormData.years_practicing;

      const response = await fetch(API_ENDPOINTS.UPDATE_THERAPIST_PROFILE, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Update failed:", errorData);
        throw new Error(errorData.error || "Failed to update profile");
      }

      const data = await response.json();

      // Update local user state with new profile data
      if (user && "profile_data" in user) {
        user.profile_data = data.profile_data;
      }

      setIsEditing(false);
      setEditFormData(null);
      alert("Profile updated successfully!");
      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="provider-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Welcome back, {displayName}</h1>
          <p className="role-tag">
            Therapist
          </p>
        </div>
        <div className="header-actions">
          {!isEditing && (
            <button className="edit-profile-button" onClick={handleEdit}>
              Edit Profile
            </button>
          )}
          {isEditing && (
            <>
              <button
                className="save-profile-button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className="cancel-edit-button"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </>
          )}
          <button className="logout-button" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      {/* Profile Section - Displays baseline fields (profile_data) */}
      <div className="profile-section">
        <div className="profile-header">
          <div className="profile-avatar">
            <img
              src={displayPhoto || defaultAvatar}
              alt={displayName}
              style={displayPhoto ? { objectFit: "cover" } : {}}
            />
          </div>
          <div className="profile-info">
            {isEditing && editFormData ? (
              <>
                <div className="edit-field">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={editFormData.name || ""}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, name: e.target.value })
                    }
                  />
                </div>
                <div className="edit-field">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={editFormData.title || ""}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, title: e.target.value })
                    }
                  />
                </div>
                <div className="edit-field">
                  <label>Phone:</label>
                  <input
                    type="text"
                    value={editFormData.phone || ""}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, phone: e.target.value })
                    }
                  />
                </div>
                <div className="edit-field">
                  <label>Location:</label>
                  <input
                    type="text"
                    value={editFormData.location || ""}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, location: e.target.value })
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <h2>{displayName}</h2>
                <p className="profile-role">
                  Therapist
                </p>
                <p className="profile-title">{displayTitle}</p>
                <p className="profile-email">{user?.email}</p>
                {displayLocation && <p className="profile-location">📍 {displayLocation}</p>}
                {displayPhone && <p className="profile-phone">📞 {displayPhone}</p>}
              </>
            )}
          </div>
        </div>

        <div className="profile-details">
          <div className="detail-item">
            <span className="detail-label">Status</span>
            <span className="detail-value online">Online</span>
          </div>
          {(displayYearsExperience || isEditing) && (
            <div className="detail-item">
              <span className="detail-label">Years Practicing</span>
              {isEditing && editFormData ? (
                <input
                  type="text"
                  className="detail-edit-input"
                  value={editFormData.years_practicing || ""}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      years_practicing: e.target.value,
                    })
                  }
                />
              ) : (
                <span className="detail-value">{displayYearsExperience} years</span>
              )}
            </div>
          )}
          <div className="detail-item">
            <span className="detail-label">Member Since</span>
            <span className="detail-value">January 2024</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Response Rate</span>
            <span className="detail-value">98%</span>
          </div>
        </div>

        {(displaySelfDescription || isEditing) && (
          <div className="self-description">
            <h3>About Me</h3>
            {isEditing && editFormData ? (
              <textarea
                value={editFormData.self_description || ""}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    self_description: e.target.value,
                  })
                }
                rows={6}
                className="edit-textarea"
              />
            ) : (
              <p>{displaySelfDescription}</p>
            )}
          </div>
        )}

        <div className="professional-section">
          <h3>Professional Qualifications</h3>

          {displayEducation && displayEducation.length > 0 && (
            <div className="education">
              <h4>Education Background</h4>
              <div className="education-list">
                {displayEducation.map((edu: any, index: number) => (
                  <div key={index} className="education-item">
                    <span className="degree">
                      {typeof edu === 'string'
                        ? edu
                        : `${edu.school || ''} - ${edu.degree || ''} in ${edu.field_of_study || ''}, ${edu.graduation_year || ''}`
                      }
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {displayTherapyMethods && displayTherapyMethods.length > 0 && (
            <div className="therapy-methods">
              <h4>Treatment Approaches</h4>
              <div className="method-list">
                {displayTherapyMethods.map((method: string, index: number) => (
                  <span key={index} className="method-tag">
                    {method}
                  </span>
                ))}
              </div>
            </div>
          )}

          {displaySpecialties && displaySpecialties.length > 0 && (
            <div className="specialties">
              <h4>Areas of Expertise</h4>
              <div className="specialty-list">
                {displaySpecialties.map((specialty: string, index: number) => (
                  <span key={index} className="specialty-tag">
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          {displayLanguages && displayLanguages.length > 0 && (
            <div className="languages">
              <h4>Languages Spoken</h4>
              <div className="method-list">
                {displayLanguages.map((lang: string, index: number) => (
                  <span key={index} className="method-tag">
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          )}

          {displayInsurance && displayInsurance.length > 0 && (
            <div className="insurance">
              <h4>Insurance Accepted</h4>
              <div className="method-list">
                {displayInsurance.map((ins: string, index: number) => (
                  <span key={index} className="method-tag">
                    {ins}
                  </span>
                ))}
              </div>
            </div>
          )}

          {displayFees && Object.keys(displayFees).length > 0 && (
            <div className="fees">
              <h4>Session Fees</h4>
              <div className="fee-list">
                {Object.entries(displayFees).map(([type, value]) => {
                  // Skip null/empty values
                  if (!value) return null;

                  return (
                    <div key={type} className="fee-item">
                      <span className="fee-type">{type}:</span>
                      <span className="fee-value">{String(value)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard Section - Analytics & Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Monthly Income</h3>
          <div className="stat-value">${providerStats.monthlyIncome}</div>
          <p className="stat-description">8% increase from last month</p>
        </div>

        <div className="stat-card">
          <h3>Monthly Sessions</h3>
          <div className="stat-value">{providerStats.monthlySessionCount}</div>
          <p className="stat-description">Average 1.5 sessions per day</p>
        </div>

        <div className="stat-card highlight">
          <h3>Pending Withdrawal</h3>
          <div className="stat-value">${providerStats.pendingWithdrawal}</div>
          <button className="withdraw-button">Request Withdrawal</button>
        </div>

        <div className="stat-card">
          <h3>Total Clients</h3>
          <div className="stat-value">{providerStats.totalClients}</div>
          <p className="stat-description">35 active clients</p>
        </div>

        <div className="stat-card">
          <h3>Average Rating</h3>
          <div className="stat-value">{providerStats.averageRating}/5.0</div>
          <p className="stat-description">Based on 50 reviews</p>
        </div>

        <div className="stat-card">
          <h3>Total Sessions</h3>
          <div className="stat-value">{providerStats.completedSessions}</div>
          <p className="stat-description">Total duration: 675 hours</p>
        </div>
      </div>

      <div className="recent-activity">
        <h2>Recent Activity</h2>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-time">Today 14:30</div>
            <div className="activity-content">
              Completed session with Mr. Zhang - 60 minutes
            </div>
          </div>
          <div className="activity-item">
            <div className="activity-time">Yesterday 16:00</div>
            <div className="activity-content">Received a new 5-star review</div>
          </div>
          <div className="activity-item">
            <div className="activity-time">Yesterday 10:15</div>
            <div className="activity-content">
              Completed session with Mrs. Li - 45 minutes
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainProvider;
